<template>
  <!-- eslint-disable vue/no-lone-template -->
  <div class="label-selector">
    <div class="label-header">
      <div class="label-title">เลือก Label</div>
      <div class="label-subtitle">ค้นหาและเลือก Label (Multi-select)</div>
    </div>
    <el-select
      :key="'select-' + language"
      v-model="selectedLabelIds"
      :disabled="disabled"
      :loading="isLoading"
      multiple
      filterable
      clearable
      placeholder="พิมพ์เพื่อค้นหา Label.."
      class="label-select"
      @change="handleChange"
      @remove-tag="handleRemoveTag"
    >
      <template #tag="{ value: tagValue, close }">
        <el-tag
          :key="'tag-' + tagValue + '-' + language"
          size="small"
          closable
          @close="close"
        >
          {{ getTagDisplayName(tagValue) }}
        </el-tag>
      </template>
      <el-option
        v-for="label in availableLabels"
        :key="label.id"
        :label="getLabelDisplayName(label)"
        :value="label.id"
      />
    </el-select>
  </div>
</template>

<script>
import { Select, Option } from "element-ui"
import { backendAdminClient } from "@/dependencies"
import { localization } from "@/lib/mapper"

export default {
  name: "LabelSelector",
  components: {
    "el-select": Select,
    "el-option": Option,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    language: {
      type: String,
      default: localization.TH,
    },
  },
  data() {
    return {
      selectedLabelIds: [],
      availableLabels: [],
      isLoading: false,
      error: null,
    }
  },
  computed: {
    selectedLabels() {
      return this.availableLabels.filter((label) =>
        this.selectedLabelIds.includes(label.id),
      )
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.selectedLabelIds = newValue || []
      },
    },
  },
  created() {
    this.fetchLabels()
  },
  methods: {
    async fetchLabels() {
      this.isLoading = true
      this.error = null

      try {
        const labels = await backendAdminClient.subscriptions.label.listAll()

        this.availableLabels = labels
      } catch (error) {
        this.error = error.message || "Failed to load labels"
        this.availableLabels = []

        this.$message.error(this.error)
      } finally {
        this.isLoading = false
      }
    },
    getNameByLanguage(label) {
      const nameMap = {
        [localization.TH]: label.nameTh,
        [localization.EN]: label.nameEn,
        [localization.CN]: label.nameZh,
      }
      return nameMap[this.language] || "-"
    },
    formatDisplayName(name) {
      return name && name.trim() ? name.trim() : "-"
    },
    getLabelDisplayName(label) {
      return this.formatDisplayName(this.getNameByLanguage(label))
    },
    getTagDisplayName(labelId) {
      const label = this.availableLabels.find((l) => l.id === labelId)

      return label ? this.getLabelDisplayName(label) : "-"
    },
    handleChange(value) {
      this.selectedLabelIds = value

      this.$emit("input", value)
      this.$emit("change", value, this.selectedLabels)
    },
    handleRemoveTag(tag) {
      const newValue = this.selectedLabelIds.filter((id) => id !== tag)
      this.handleChange(newValue)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.label-selector {
  width: 100%;
  margin-bottom: 16px;
}

.label-header {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: baseline;
  margin-bottom: 4px;
}

.label-title {
  font-size: 1rem;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.label-subtitle {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.label-select {
  width: 100%;

  ::v-deep .el-select__tags {
    max-width: 100%;
    padding: 8px;
    gap: 2px;
  }

  ::v-deep .el-tag {
    max-width: calc(100% - 32px);
    background-color: #f5f7fa !important;
    color: #343a40 !important;
    border: 1px solid #e4e7ed !important;
    border-radius: 20px;
    padding: 4px 8px;
    font-size: 14px;

    &:hover {
      transform: translateY(-1px);
    }

    .el-tag__close {
      color: white;
      font-size: 14px;
      font-weight: 700;
      margin-left: 6px;
      background-color: #6c757d;

      &:hover {
        color: white;
        background-color: #6c757d;
        border-radius: 50%;
      }
    }
  }

  ::v-deep .el-select__input {
    width: 100px !important;
  }
}

// Responsive design
@media (max-width: 768px) {
  .label-title {
    font-size: 13px;
  }

  .label-subtitle {
    font-size: 11px;
  }
}
</style>
